import { useState, useEffect } from "react";
import { doc, serverTimestamp, runTransaction } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import { THEME_COLOR, LEVELS } from "../../config/club";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function EditProfileModal({
    user,
    setUser,
    profile,
    onClose,
    forceUsername = false,
}) {
    const [name, setName] = useState(profile?.displayName || user.displayName || "");
    const [dupr, setDupr] = useState(profile?.dupr || user.dupr || "");
    const [username, setUsername] = useState(profile?.username || "");
    const [bio, setBio] = useState(profile?.bio || "");
    const [level, setLevel] = useState(profile?.level || 1);
    const [saving, setSaving] = useState(false);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(profile?.photoURL || "/img/rrc2.png");

    const storage = getStorage();
    const THEME_VIOLET = THEME_COLOR;

    useEffect(() => {
        return () => {
            if (avatarPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(avatarPreview);
            }
        };
    }, [avatarPreview]);
    useEffect(() => {
        if (profile?.photoURL) {
            setAvatarPreview(profile.photoURL);
        }
    }, [profile]);
    const saveProfile = async () => {
        if (forceUsername && !username) return Swal.fire("Required", "Please choose a username", "warning");
        const clean = username.trim().toLowerCase();
        if (clean && clean.length < 3) return Swal.fire("Too Short", "Username needs 3+ characters", "warning");
        if (bio.length > 150) {
            return Swal.fire("Too Long", "Bio must be under 150 characters.", "warning");
        }
        setSaving(true);
        try {
            let photoURL = profile?.photoURL || null;
            if (avatarFile) {
                const avatarRef = ref(storage, `avatars/${user.uid}/avatar.jpg`);
                await uploadBytes(avatarRef, avatarFile);
                photoURL = await getDownloadURL(avatarRef);
            }

            await runTransaction(db, async (tx) => {
                const userRef = doc(db, "users", user.uid);
                if (!profile?.username && clean) {
                    const usernameRef = doc(db, "usernames", clean);
                    const unameSnap = await tx.get(usernameRef);
                    if (unameSnap.exists()) throw new Error("Username already taken");
                    tx.set(usernameRef, { uid: user.uid, createdAt: serverTimestamp() });
                }
                tx.update(userRef, {
                    displayName: name,
                    dupr: dupr,
                    level,
                    photoURL,
                    username: profile?.username || clean || null,
                    bio: bio.trim() || null,
                    updatedAt: serverTimestamp(),
                });
            });
            setUser(prevUser => ({
                ...prevUser,
                level,
                dupr,
                displayName: name,
                username: clean,
                bio: bio.trim()
            }));
            onClose({ username: profile?.username || clean });
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center px-3"
            style={{ zIndex: 1045, backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}>

            <div
                className="rounded-4 shadow-lg w-100 position-relative overflow-hidden"
                style={{
                    maxWidth: 450,
                    backgroundColor: "var(--card-bg)",
                    color: "var(--text-color)"
                }}
            >
                {/* Header Accent */}
                <div style={{ height: '6px', backgroundColor: THEME_VIOLET }}></div>

                <div className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="fw-black mb-0" style={{ color: THEME_VIOLET }}>Edit Profile</h4>
                        {!forceUsername && (
                            <button className="btn-close" onClick={() => onClose(false)}></button>
                        )}
                    </div>

                    {/* Avatar Section */}
                    <div className="text-center mb-4">
                        <div className="position-relative d-inline-block">
                            <img
                                src={avatarPreview || "/img/rrc2.png"}
                                alt="Avatar"
                                className="rounded-circle shadow-sm border border-4"
                                style={{
                                    width: 100,
                                    height: 100,
                                    objectFit: "cover",
                                    borderColor: "var(--card-bg)"
                                }}
                                onError={(e) => {
                                    e.target.src = "/img/rrc2.png";
                                }}
                            />
                            <label htmlFor="avatarUpload" className="position-absolute bottom-0 end-0 btn btn-sm btn-dark rounded-circle p-1 shadow">
                                <i className="bi bi-camera-fill px-1"></i>
                            </label>
                            <input id="avatarUpload" type="file" hidden accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setAvatarFile(file);
                                        setAvatarPreview(URL.createObjectURL(file));
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* Inputs - Matching JoinClub Style */}
                    <div className="mb-3">
                        <label className="form-label fw-bold small text-muted text-uppercase">DUPR Display Name</label>
                        <input
                            className="form-control form-control-lg rounded-3 custom-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold small text-muted text-uppercase">DUPR ID (Optional)</label>
                        <input
                            className="form-control form-control-lg rounded-3 custom-input"
                            value={dupr}
                            onChange={(e) => setDupr(e.target.value)}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-bold small text-muted text-uppercase">
                            Bio
                        </label>
                        <textarea
                            className="form-control rounded-3 custom-input"
                            rows="3"
                            maxLength={150}
                            placeholder="Add a short bio..."
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                        />
                        <div className="form-text small opacity-75 text-end">
                            {bio.length}/150
                        </div>
                    </div>
                    {!profile?.username && (
                        <div className="mb-3">
                            <label className="form-label fw-bold small text-muted text-uppercase">Username</label>
                            <input
                                className="form-control form-control-lg rounded-3 custom-input"
                                value={username}
                                placeholder="eg. rrc_astig"
                                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                            />
                            <div className="form-text small opacity-75">Used for your profile link. Permanent.</div>
                        </div>
                    )}

                    <label className="form-label fw-bold small text-muted text-uppercase">Skill Level</label>
                    <div className="row g-2 mb-4">
                        {LEVELS.map(l => {
                            const isActive = level === l.value;

                            return (
                                <div key={l.value} className="col-6">
                                    <button
                                        type="button"
                                        onClick={() => setLevel(l.value)}
                                        className="w-100 py-2 small fw-bold rounded-3 border-0"
                                        style={{
                                            backgroundColor: isActive
                                                ? THEME_VIOLET
                                                : document.body.dataset.theme === "dark"
                                                    ? "#2a2a2a"
                                                    : "#f8f9fa",
                                            color: isActive
                                                ? "#ffffff"
                                                : document.body.dataset.theme === "dark"
                                                    ? "#ffffff"
                                                    : "#212529",
                                            border: isActive
                                                ? `1px solid ${THEME_VIOLET}`
                                                : document.body.dataset.theme === "dark"
                                                    ? "1px solid #3a3a3a"
                                                    : "1px solid #dee2e6",
                                            transition: "all 0.2s ease"
                                        }}
                                    >
                                        {l.label.split('–')[1] || l.label}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Action Buttons */}
                    <button
                        className="btn btn-lg w-100 fw-bold text-white rounded-pill shadow-sm py-3 mb-2"
                        style={{ backgroundColor: THEME_VIOLET }}
                        onClick={saveProfile}
                        disabled={saving}
                    >
                        {saving ? "Saving Changes..." : "Save Changes"}
                    </button>

                    {!forceUsername && (
                        <button className="btn btn-link w-100 text-muted text-decoration-none small" onClick={() => onClose(false)}>
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                .fw-black { font-weight: 900; }
                .transition-all { transition: all 0.2s ease; }
            `}</style>
        </div>
    );
}